<template>
  <div class="flex flex-col p-3">

    <AppModal v-model="showNewService" title="New Document Cost" hide-footer>
      <div class="space-y-3">
        <input type="text"
               class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-sm"
               v-model="form.documentNumber" placeholder="Document Number" />
        <input type="text"
               class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-sm"
               v-model="form.documentName" placeholder="Document Name" />
        <input type="number"
               class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-sm"
               v-model="form.amount" placeholder="Document Cost" />
        <select v-model="form.currencyId"
                class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-white">
          <option value="" disabled selected>Select Currency</option>
          <option v-for="(item, idx) in currencies" :key="idx" :value="item.id">{{ item.code }} - {{
              item.name
            }}
          </option>
        </select>
        <div class="flex justify-end">
          <button class="btn-primary" @click="save">
            <i class="fa fa-save mr-1"></i> Save
          </button>
        </div>
      </div>
    </AppModal>

    <AppModal v-model="showEditService" title="Edit Document Cost" hide-footer>
      <div class="space-y-3">
        <input type="text"
               class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-sm"
               v-model="editForm.documentName" placeholder="Document Name" />
        <input type="text"
               class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-sm"
               v-model="editForm.documentNumber" placeholder="Document Number" />
        <input type="number"
               class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-sm"
               v-model="editForm.amount" placeholder="Document Cost" />
        <select v-model="editForm.currencyId"
                class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-white">
          <option value="" disabled selected>Select Currency</option>
          <option v-for="(item, idx) in currencies" :key="idx" :value="item.id">{{ item.code }} - {{
              item.name
            }}
          </option>
        </select>
        <div class="flex justify-end">
          <button class="btn-primary" @click="updateDocumentCost">
            <i class="fa fa-pencil mr-1"></i> Save Changes
          </button>
        </div>
      </div>
    </AppModal>

    <!-- Header -->
    <div class="flex items-center justify-between py-3">
      <input
        type="text"
        v-model="search"
        class="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-sm w-64"
        placeholder="Search by name"
      />
      <button class="btn-primary" @click="showNewService = true">
        <i class="fa fa-plus mr-1"></i> Add Document Cost
      </button>
    </div>

    <!-- Table -->
    <div>
      <table>
        <thead>
        <tr>
          <th>#</th>
          <th>Document Number</th>
          <th>Document Name</th>
          <th>Fee Category</th>
          <th>Cost</th>
          <th>Action</th>
        </tr>
        </thead>
        <tbody>
        <template v-if="loading">
          <tr v-for="i in 5" :key="`sk-${i}`" class="animate-pulse">
            <td>
              <div class="h-3 bg-gray-200 rounded w-6"></div>
            </td>
            <td>
              <div class="h-3 bg-gray-200 rounded w-24"></div>
            </td>
            <td>
              <div class="h-3 bg-gray-200 rounded w-40"></div>
            </td>
            <td>
              <div class="h-3 bg-gray-200 rounded w-20"></div>
            </td>
            <td>
              <div class="h-3 bg-gray-200 rounded w-16"></div>
            </td>
            <td>
              <div class="h-3 bg-gray-200 rounded w-20"></div>
            </td>
          </tr>
        </template>
        <template v-else>
          <tr v-for="(service, idx) in serviceCosts" :key="service.id">
            <td>{{ idx + 1 }}</td>
            <td class="font-bold font-mono">{{ service.documentNumber }}</td>
            <td>{{ service.documentName }}</td>
            <td><span class="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">Certification</span></td>
            <td class="font-medium">UGX {{ service.amount?.toLocaleString() }}</td>
            <td>
              <div class="flex gap-1">
                <button class="btn-sm-primary" @click="openEdit(service)"><i class="fa fa-edit mr-1"></i>Edit</button>
                <button class="btn-sm-danger" @click="deleteService(service.id)"><i class="fa fa-trash mr-1"></i>Delete
                </button>
              </div>
            </td>
          </tr>
        </template>
        <tr v-if="!loading && serviceCosts.length === 0">
          <td colspan="6" class="text-center py-6 text-gray-400 text-sm">No results found</td>
        </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from "vue";
import AppModal from "@/components/ui/AppModal.vue";
import { useBillingStore } from "@/domains/home/services/billing/stores";

const billingStore = useBillingStore();

const showNewService = ref(false);
const showEditService = ref(false);
const loading = ref(false);
const search = ref("");
const serviceCosts = ref<any[]>([]);
const currencies = ref<any[]>([]);

const form = ref({
  documentNumber: null as string | null,
  documentName: null as string | null,
  amount: null as number | null,
  currencyId: null as string | null
});
const editForm = ref({
  id: null as string | null,
  documentNumber: null as string | null,
  documentName: null as string | null,
  amount: null as number | null,
  currencyId: null as string | null
});

onMounted(() => {
  loading.value = true;
  billingStore.fetchDocumentCosts();
  billingStore.fetchCurrencies();
});

const save = () => {
  billingStore.createDocumentCost(form.value);
};

const openEdit = (data: any) => {
  editForm.value = { ...data };
  showEditService.value = true;
};

const updateDocumentCost = () => {
  loading.value = true;
  billingStore.editDocumentCost(editForm.value);
};

const deleteService = (id: string) => {
  loading.value = true;
  billingStore.deleteServiceCost(id);
};

const resetForm = () => {
  form.value = { documentNumber: null, documentName: null, amount: null, currencyId: null };
};

watch(() => billingStore.currencies, (data) => {
  if (data?.data) currencies.value = data.data;
  else if (Array.isArray(data)) currencies.value = data;
});

watch(() => billingStore.documentCosts, (data) => {
  loading.value = false;
  if (data?.data) serviceCosts.value = data.data;
  else if (Array.isArray(data)) serviceCosts.value = data;
});

watch(() => billingStore.documentCost, (data) => {
  if (data) {
    resetForm();
    showNewService.value = false;
    loading.value = true;
    billingStore.fetchDocumentCosts();
  }
});

watch(() => billingStore.editDocumentCostResult, (data) => {
  if (data) {
    showEditService.value = false;
    loading.value = true;
    billingStore.fetchDocumentCosts();
  }
});

watch(() => billingStore.deleteServiceCostResult, (data) => {
  if (data) {
    loading.value = true;
    billingStore.fetchDocumentCosts();
  }
});

watch(() => billingStore.error, (data) => {
  if (data) loading.value = false;
});
</script>
